import { SignInButton, UserButton, useAuth, useUser } from "@clerk/react";
import { useEffect, useState } from "react";

import type { AccountFeature, AccountPlan } from "../lib/auth/entitlements";

interface AccountResponse {
  authenticated: true;
  userId: string;
  plan: AccountPlan;
  features: AccountFeature[];
  storageMode: "d1" | "session-only";
}

const clerkEnabled = Boolean(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY?.trim());

function GuestAccountControl() {
  return (
    <div className="account-control guest" aria-label="게스트 계정">
      <span className="account-copy">
        <strong>게스트</strong>
        <small>이 기기에만 저장 · <a href="/legal/privacy.html" target="_blank" rel="noreferrer">개인정보</a></small>
      </span>
      <span className="plan-badge">FREE</span>
    </div>
  );
}

function ClerkAccountControl() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const [account, setAccount] = useState<AccountResponse | null>(null);
  const [accountState, setAccountState] = useState<"idle" | "loading" | "ready" | "unavailable">("idle");

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setAccount(null);
      setAccountState("idle");
      return;
    }

    const controller = new AbortController();
    setAccount(null);
    setAccountState("loading");
    void (async () => {
      try {
        const token = await getToken();
        const response = await fetch("/api/me", {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`account api ${response.status}`);
        setAccount(await response.json() as AccountResponse);
        setAccountState("ready");
      } catch (error) {
        if ((error as Error).name !== "AbortError") setAccountState("unavailable");
      }
    })();
    return () => controller.abort();
  }, [getToken, isLoaded, isSignedIn, user?.id]);

  if (!isLoaded) return <div className="account-skeleton" aria-label="계정 확인 중" />;
  if (!isSignedIn) {
    return (
      <SignInButton mode="modal">
        <button className="sign-in-button" type="button">로그인</button>
      </SignInButton>
    );
  }

  const label = user?.firstName || user?.primaryEmailAddress?.emailAddress || "로그인 사용자";
  const planLabel = accountState === "ready"
    ? (account?.plan ?? "free").toUpperCase()
    : accountState === "loading"
      ? "확인 중"
      : "확인 필요";
  return (
    <div className="account-control" aria-label="로그인 계정">
      <span className="account-copy">
        <strong>{label}</strong>
        <small>{accountState === "unavailable" ? "권한 서버 확인 필요" : "로컬 데이터 유지"}</small>
      </span>
      <span className={`plan-badge ${account?.plan === "pro" ? "pro" : ""}`}>
        {planLabel}
      </span>
      <UserButton showName={false} />
    </div>
  );
}

export function AccountControl() {
  return clerkEnabled ? <ClerkAccountControl /> : <GuestAccountControl />;
}
